import React, { useState, useCallback, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { supabase } from "../lib/supabase-jsonb";
import { useCharacterGenerationRealtime } from "../hooks/useCharacterGenerationRealtime";
import { RealtimeStatus } from "./RealtimeStatus";
import PaywallModal from "./PaywallModal";
import { uploadFileToStorageWithRetry } from "../utils/uploadWithRetry";
import { FileUploadArea } from "./common/uiHelper";
import { Button } from "./ui/button";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import CharacterGenerationDebugger from "../utils/debugCharacterGeneration";
import { GenerationProgress } from "./GenerationProgress";
import { CHARACTER_STAGES, INPUT_QUALITY_TIPS, MAX_UPLOAD_BYTES } from "../constants";
import {
  NotifyPermissionState,
  clearTabAttention,
  flagTabAttention,
  getNotifyState,
  requestNotifyPermission,
  showCompletionNotification,
} from "../utils/notify";

interface Props {
  setPendingJob: (jobId: string | null) => void;
  setMeshLoading: (loading: boolean) => void;
  characterGenerating: boolean;
  setCharacterGenerating: (loading: boolean) => void;
  onStartGeneration: () => void;
  onGenerationComplete?: (resultUrl: string) => void;
}

const CharacterGeneratorRefactored: React.FC<Props> = ({ 
  setPendingJob, 
  setMeshLoading, 
  characterGenerating, 
  setCharacterGenerating, 
  onStartGeneration,
  onGenerationComplete
}) => {
  const [images, setImages] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // Epoch ms when the current generation started — drives the progress panel
  const [startedAt, setStartedAt] = useState<number | null>(null);
  // Inline failure message paired with a one-click retry (images stay loaded)
  const [localError, setLocalError] = useState<string | null>(null);
  const [notifyState, setNotifyState] = useState<NotifyPermissionState>(getNotifyState);
  const { currentUser } = useAuth();
  const [paywallModal, setPaywallModal] = useState(false);
  const debuggerRef = useRef<CharacterGenerationDebugger | null>(null);

  // Use the new realtime hook
  const { 
    status: generationStatus,
    isConnected,
  } = useCharacterGenerationRealtime(currentJobId, sessionId);

  const showToastMessage = useCallback((message: string) => {
    toast(message);
  }, []);

  const handleGenerationSuccess = useCallback((resultUrl: string) => {
    console.log('[CharacterGenerator] Generation successful:', resultUrl);
    setCharacterGenerating(false);
    setMeshLoading(false);
    setPendingJob(null);
    setCurrentJobId(null);
    setStartedAt(null);
    setLocalError(null);
    onGenerationComplete?.(resultUrl);

    // Clear images after success
    setImages([]);
    setPreviews([]);
  }, [setCharacterGenerating, setMeshLoading, setPendingJob, onGenerationComplete]);

  const handleGenerationError = useCallback((errorMessage: string) => {
    console.error('[CharacterGenerator] Generation failed:', errorMessage);
    setCharacterGenerating(false);
    setMeshLoading(false);
    setPendingJob(null);
    setCurrentJobId(null);
    setStartedAt(null);
    setLocalError(errorMessage);
  }, [setCharacterGenerating, setMeshLoading, setPendingJob]);

  const handleNotifyClick = useCallback(async () => {
    setNotifyState(await requestNotifyPermission());
  }, []);

  // Handle generation completion
  useEffect(() => {
    if (generationStatus?.status === 'DONE' && generationStatus.resultUrl) {
      debuggerRef.current?.log('generation_complete', 'success', {
        resultUrl: generationStatus.resultUrl
      });
      // Pull users back if they tabbed away during the wait
      flagTabAttention("success");
      showCompletionNotification(true);
      handleGenerationSuccess(generationStatus.resultUrl);
    } else if (generationStatus?.status === 'ERROR') {
      debuggerRef.current?.log('generation_error', 'error', {
        error: generationStatus.errorMessage
      });
      flagTabAttention("error");
      showCompletionNotification(false);
      handleGenerationError(generationStatus.errorMessage || 'Generation failed');
    } else if (generationStatus?.status === 'PROCESSING') {
      debuggerRef.current?.log('generation_processing', 'success', {
        status: 'PROCESSING'
      });
    }
  }, [generationStatus, handleGenerationError, handleGenerationSuccess]);

  const handleMultipleFilesSelect = useCallback((files: File[]) => {
    const typedFiles = files.filter(
      (file) => file.type === "image/png" || file.type === "image/jpeg"
    );

    if (typedFiles.length === 0) {
      showToastMessage("Please upload PNG or JPG images");
      return;
    }
    if (typedFiles.length < files.length) {
      showToastMessage("Some files were skipped — only PNG and JPG images are supported");
    }

    const validFiles = typedFiles.filter((file) => {
      if (file.size <= MAX_UPLOAD_BYTES) return true;
      showToastMessage(`"${file.name}" is over 20MB — please use a smaller image`);
      return false;
    });
    if (validFiles.length === 0) return;

    setImages(validFiles);
    const previewUrls = validFiles.map((file) => URL.createObjectURL(file));
    setPreviews(previewUrls);
  }, [showToastMessage]);

  const selectExampleImages = useCallback(async () => {
    const exampleUrls = [
      "/turnaround-ex1-1.png",
      "/turnaround-ex1-2.png",
      "/turnaround-ex1-3.png",
    ];

    try {
      const fetchedFiles: File[] = [];
      for (const url of exampleUrls) {
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`Failed to fetch ${url}`);
        }
        const blob = await res.blob();
        const filename = url.split("/").pop() || "example.png";
        const file = new File([blob], filename, { type: blob.type });
        fetchedFiles.push(file);
      }

      handleMultipleFilesSelect(fetchedFiles);
    } catch (error) {
      console.error("Error fetching example images:", error);
      showToastMessage("Failed to load example images.");
    }
  }, [showToastMessage, handleMultipleFilesSelect]);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const droppedFiles = Array.from(e.dataTransfer.files);
      handleMultipleFilesSelect(droppedFiles);
    },
    [handleMultipleFilesSelect]
  );

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        handleMultipleFilesSelect(Array.from(e.target.files));
      }
    },
    [handleMultipleFilesSelect]
  );

  const generateCharacter = async () => {
    if (images.length === 0) {
      showToastMessage("Please provide images first");
      return;
    }

    onStartGeneration();
    setMeshLoading(true);
    setCharacterGenerating(true);
    setLocalError(null);
    setStartedAt(Date.now());
    clearTabAttention();

    // Initialize debugger
    debuggerRef.current = new CharacterGenerationDebugger();

    if (!currentUser) {
      showToastMessage("Please sign in to generate characters");
      setPaywallModal(true);
      setMeshLoading(false);
      setCharacterGenerating(false);
      setStartedAt(null);
      return;
    }

    try {
      // Upload images with retry logic and better error handling
      const uploadPromises = images.map((image, index) =>
        uploadFileToStorageWithRetry(
          image, 
          `${currentUser.uid}/image_${Date.now()}_${index}.png`,
          {
            maxRetries: 3,
            timeout: 120000, // 2 minutes for large images
            onRetry: (attempt, error) => {
              console.log(`[CharacterGenerator] Retrying upload (attempt ${attempt + 1}):`, error.message);
              showToastMessage(`Retrying upload (attempt ${attempt + 1})...`);
            },
            onProgress: (progress) => {
              console.log(`[CharacterGenerator] Upload progress: ${progress.toFixed(0)}%`);
            }
          }
        )
      );
      
      let imageUrls: string[];
      try {
        imageUrls = await Promise.all(uploadPromises);
      } catch (uploadError) {
        console.error('[CharacterGenerator] Upload failed:', uploadError);
        if (uploadError instanceof Error) {
          if (uploadError.message.includes('Network error') || uploadError.message.includes('Failed to fetch')) {
            showToastMessage("Network error: Please check your internet connection and try again");
          } else if (uploadError.message.includes('timeout')) {
            showToastMessage("Upload timed out. Please try with smaller images or check your connection");
          } else {
            showToastMessage(`Upload failed: ${uploadError.message}`);
          }
        } else {
          showToastMessage("Failed to upload images. Please try again.");
        }
        throw uploadError;
      }
      
      // Generate session ID for this generation
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      setSessionId(newSessionId);
      
      console.log("[CharacterGenerator] Creating generation with session:", newSessionId);
      
      // Use the secure function to create generation
      const { data, error } = await (supabase as any).rpc('create_character_generation_secure', {
        p_user_id: currentUser.uid,
        p_images: JSON.stringify(imageUrls),
        p_session_id: newSessionId
      });

      if (error) {
        throw error;
      }

      const generationId = data;
      console.log("[CharacterGenerator] Generation created:", generationId);
      
      // Initialize debugger with generation ID
      debuggerRef.current?.startDebug(generationId);
      debuggerRef.current?.log('generation_created', 'success', { 
        generationId, 
        sessionId: newSessionId 
      });
      
      // Set current job for tracking
      setCurrentJobId(generationId);
      setPendingJob(generationId);
      
      // Trigger RunPod generation via edge function
      console.log('[CharacterGenerator] Triggering RunPod generation...');
      debuggerRef.current?.log('triggering_edge_function', 'success', { 
        url: `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/trigger-character-generation` 
      });
      
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/trigger-character-generation`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          record: {
            id: generationId,
            user_id: currentUser.uid,
            session_id: newSessionId,
            images: imageUrls,
            status: 'PENDING'
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        debuggerRef.current?.log('edge_function_failed', 'error', {
          status: response.status,
          error: errorText
        });
        throw new Error(
          `Generation service is unavailable right now (HTTP ${response.status}). Please try again.`
        );
      }

      const responseData = await response.json();
      debuggerRef.current?.log('edge_function_success', 'success', {
        response: responseData
      });
      console.log('[CharacterGenerator] RunPod triggered successfully');
      toast.success('Generation started! Processing your images...');

      // Run diagnostics after a delay to check status
      setTimeout(() => {
        if (debuggerRef.current) {
          debuggerRef.current.runFullDiagnostics();
        }
      }, 3000);


    } catch (error) {
      debuggerRef.current?.log('generation_failed', 'error', { error });
      console.error("[CharacterGenerator] Error:", error);
      handleGenerationError(error instanceof Error ? error.message : 'Unknown error');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previews]);

  const getStatusDisplay = () => {
    if (!generationStatus) return null;
    
    switch (generationStatus.status) {
      case 'PENDING':
        return (
          <div className="flex items-center gap-2 text-warning">
            <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" />
            <span>Queued for processing...</span>
          </div>
        );
      case 'PROCESSING':
        return (
          <div className="flex items-center gap-2 text-main">
            <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" />
            <span>Generating your character model...</span>
          </div>
        );
      case 'DONE':
        return (
          <div className="flex items-center gap-2 text-success">
            <CheckCircle className="w-4 h-4" />
            <span>Generation complete!</span>
          </div>
        );
      case 'ERROR':
        return (
          <div className="flex items-center gap-2 text-danger">
            <XCircle className="w-4 h-4" />
            <span>Generation failed</span>
          </div>
        );
    }
  };

  return (
    <div className="space-y-4">
      {/* Real-time connection status */}
      {currentJobId && (
        <div className="flex justify-between items-center p-2 bg-secondary-background border-2 border-border rounded-lg">
          <RealtimeStatus
            status={isConnected ? 'connected' : 'disconnected'}
            error={null}
            showDetails={false}
          />
          {getStatusDisplay()}
        </div>
      )}

      {/* File upload area */}
      <FileUploadArea
        onDrop={handleDrop}
        onBrowse={handleFileInput}
        fileInputId="character-upload"
        accept="image/png,image/jpeg"
        uploadText="Drop character images here or click to browse"
        previewType="image"
        previewUrls={previews}
        clearPreview={() => {
          setImages([]);
          setPreviews([]);
        }}
      />

      {/* Example images button */}
      {images.length === 0 && (
        <Button
          onClick={selectExampleImages}
          variant="outline"
          className="w-full"
          disabled={characterGenerating}
        >
          Use Example Images
        </Button>
      )}

      {/* Error message with one-click retry — uploaded images stay loaded */}
      {!characterGenerating && (localError || generationStatus?.errorMessage) && (
        <div
          role="alert"
          className="border-2 border-danger bg-danger/10 rounded-lg neo-shadow p-4 text-sm"
        >
          <p className="font-semibold text-foreground">
            {localError || generationStatus?.errorMessage}
          </p>
          {images.length > 0 && (
            <Button type="button" className="w-full mt-3" onClick={generateCharacter}>
              Try again
            </Button>
          )}
        </div>
      )}

      {/* Generate button */}
      <Button
        onClick={generateCharacter}
        disabled={characterGenerating || images.length === 0}
        className="w-full"
      >
        {characterGenerating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none" />
            Generating Character...
          </>
        ) : (
          'Generate 3D Character'
        )}
      </Button>

      {/* Staged wait experience shared with the image-to-3D flow */}
      {characterGenerating && startedAt !== null && (
        <GenerationProgress
          startedAt={startedAt}
          stages={CHARACTER_STAGES}
          imageUrls={previews}
          tips={INPUT_QUALITY_TIPS}
          pinnedStageIndex={
            !currentJobId
              ? 0 // uploading — real signal (job record not created yet)
              : generationStatus?.status === "PENDING"
                ? 1 // queued — real signal from the backend
                : null // PROCESSING — time-driven narration
          }
          notifyState={notifyState}
          onNotifyClick={handleNotifyClick}
        />
      )}

      {/* Connection troubleshooting */}
      {currentJobId && !isConnected && (
        <div className="text-xs text-foreground/60 text-center">
          Connection issues? The generation will continue in the background.
        </div>
      )}

      {/* Paywall modal */}
      <PaywallModal
        isOpen={paywallModal}
        onClose={() => setPaywallModal(false)}
        user={currentUser}
      />
    </div>
  );
};

export default CharacterGeneratorRefactored;
